import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { TemplateAttributeModel } from 'src/app/model/templateattributemodel.model';
import { AccelaControl, AccelaControlType } from 'src/app/modules/shared/model/accelaControl.model';
import { RegionalMasksDto } from 'src/app/modules/shared/model/regionalMasksDto.model';
import { SimpleViewElement } from 'src/app/modules/shared/model/simpleViewElement.model';
import { DropdownValueService } from './dropdown-value-service.service';
import { RegionalSettingsService } from './regional-settings.service';
import { HttpErrorHandler } from 'src/app/modules/shared/services/error-handler.service';
import { ExpressionResponse } from 'src/app/modules/shared/model/expressions.model';
import { SelectItem } from 'primeng/api';
import { isStringNullOrWhiteSpace } from 'src/app/util/StringUtil';
import { StringKeyValue } from 'src/app/modules/shared/model/stringKeyValue.model';
import { PasswordRequirementValidators } from 'src/app/validators/passwordRequirment.validator';
import { AccelaTextValidators } from 'src/app/validators/accelaText.validator';

@Injectable({
  providedIn: 'root'
})
export class PageLayoutService {

  headers: HttpHeaders;
  apiServer: string; //GetSimpleViewElements
  private renderer: Renderer2;

  constructor(private http: HttpClient, private apiRef: ApiProviderService, rendererFactory: RendererFactory2,
    private dropdownValueRepo: DropdownValueService, private regionalService: RegionalSettingsService,
    private passwordValidator: PasswordRequirementValidators, private emailValidator: AccelaTextValidators,
    private errorHandler: HttpErrorHandler) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.headers = new HttpHeaders();
    this.headers.append('Content-Type', 'application/json');
    this.headers.append('Accept', 'application/json');
    this.apiServer = 'api/PageLayout/';
  }

  getSimpleViewElements(viewId: string, module: string): Observable<Array<SimpleViewElement>> {
    const httpParams = new HttpParams()
      .set('ViewId', viewId)
      .set('Module', module);
    return this.http.get<Array<SimpleViewElement>>(this.apiRef
      .getApiUrl(`${this.apiServer}GetSimpleViewElements`),
      { headers: this.headers, params: httpParams })
      .pipe(catchError(this.errorHandler.handleError));

  }

  getSimpleViewElementsWithPermissions(viewId: string, module: string, permissionLevel: string, permissionValue: string, callerId: string):
    Observable<Array<SimpleViewElement>> {
    const httpParams = new HttpParams()
      .set('ViewId', viewId)
      .set('Module', module)
      .set('PermissionLevel', permissionLevel)
      .set('PermissionValue', permissionValue)
      .set('CallerId', callerId);

    return this.http.get<Array<SimpleViewElement>>(this.apiRef.getApiUrl(`${this.apiServer}GetSimpleViewElements`),
      { headers: this.headers, params: httpParams })
      .pipe(catchError(this.errorHandler.handleError));

  }

  getDynamicFormData(
    viewId: string, module: string, permissionLevel: string, permissionValue: string, callerId: string): Observable<Array<AccelaControl>> {
    const httpParams = new HttpParams()
      .set('ViewId', viewId)
      .set('Module', module)
      .set('PermissionLevel', permissionLevel)
      .set('PermissionValue', permissionValue)
      .set('CallerId', callerId);

    return this.http.get<Array<AccelaControl>>(this.apiRef.getApiUrl(`${this.apiServer}GetDynamicFormData`),
      { headers: this.headers, params: httpParams })
      .pipe(catchError(this.errorHandler.handleError));

  }

  saveUpdatedControl(controlData: AccelaControl, module: string, viewId: string) {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}UpdateControlContentProperties`,
      [{ Key: 'module', Value: module },
      { Key: 'viewId', Value: viewId }])
      , { headers: this.headers, body: controlData })
      .pipe(map(json => json as boolean));
  }

  saveUpdatedControlProperties(controlData: AccelaControl[], module: string, viewId: string,
    permissionLevel: string = '', permissionValue: string = '') {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}UpdateControlFieldProperties`,
      [{ Key: 'module', Value: module },
      { Key: 'viewId', Value: viewId },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue }])
      , { headers: this.headers, body: controlData })
      .pipe(map(json => json as boolean));
  }

  saveUpdatedSection(htmlText: string, key: string, viewId: string, module: string,
    permissionLevel: string = '', permissionValue: string = '') {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}UpdateSectionContent`,
      [{ Key: 'contentKey', Value: key },
      { Key: 'viewId', Value: viewId },
      { Key: 'module', Value: module },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue }]),
      { content: htmlText }, { headers: this.headers })
      .pipe(map(json => json as boolean));
  }

  saveLabelArray(labels: StringKeyValue[], viewId: string, module: string, permissionLevel: string = '', permissionValue: string = '') {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}SaveLabelArray`,
      [{ Key: 'viewId', Value: viewId },
      { Key: 'module', Value: module },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue }]),
      { content: labels }, { headers: this.headers })
      .pipe(map(json => json as boolean));
  }

  processAccelaControl(controlData: AccelaControl, abstractControl: AbstractControl, ddlValue?: string) {

    if (controlData.optionKey === 'Subdivision') {

      if (controlData.type === AccelaControlType.Dropdown) {
        abstractControl.controlData = controlData;
        if (!controlData.options || controlData.options.length === 0) {
          this.dropdownValueRepo.getDropdownValueFun(controlData.name, controlData.optionKey).subscribe(res => {
            if ((!isStringNullOrWhiteSpace(ddlValue))) {
              if (res.findIndex(i => i.value === ddlValue) < 0) {
                res.push({ label: ddlValue, value: ddlValue });
              }
            }

            controlData.options = res;
          });
        }
      }
    } else {

      abstractControl.controlData = controlData;
      //Fetch dropdown options
      if (controlData.type === AccelaControlType.Blocked) {
        abstractControl.clearValidators();
        return;
      } else if (controlData.type === AccelaControlType.Dropdown || controlData.type === AccelaControlType.RadioGroup) {
        if (!controlData.options || controlData.options.length === 0) {
          if (controlData.name.includes('ddlQuestion') && !controlData.name.includes('ddlQuestionForDaily')) {
            this.dropdownValueRepo.getDropdownValueFun('ddlQuestion', controlData.optionKey).subscribe(res => {
              controlData.options = res;
            });
          }
          else {
            this.dropdownValueRepo.getDropdownValueFun(controlData.name, controlData.optionKey).subscribe(res => {
              controlData.options = res;
            });
          }
        }

      } else if (controlData.type === AccelaControlType.Email) {
        if (abstractControl.validator) {
          abstractControl.setValidators([abstractControl.validator, this.emailValidator.emailValidate()]);
        } else { /* if no validators added already */
          abstractControl.setValidators([this.emailValidator.emailValidate()]);
        }
        abstractControl.updateValueAndValidity();
      } else if (controlData.type === AccelaControlType.Password && controlData.name.toLowerCase() === 'txbpassword1') {
        abstractControl.setAsyncValidators([this.passwordValidator.validate()]);
      }

      //RegionControllers are dropdowns that change localized masks when the value changes
      if (controlData.optionKey === 'RegionController') {

        abstractControl.valueChanges.subscribe(res => {
          this.updateRegionalMasks(abstractControl, res);
        });

        //Check to see if a default value has been specified
        this.regionalService.getDefaultCountry().subscribe((res) => {
          if (!isStringNullOrWhiteSpace(res)) {
            abstractControl.setValue(res);
          }
        });

      }
    }

    if (controlData.required) {
      if (abstractControl.validator) {
        abstractControl.setValidators([abstractControl.validator, Validators.required]);
      } else { /* if no validators added already */
        abstractControl.setValidators([Validators.required]);
      }
      abstractControl.updateValueAndValidity();
    }
  }

  updateRegionalMasks(abstractControl: AbstractControl, region: string) {
    if (abstractControl.parent == null) {
      return;
    }

    if (!isStringNullOrWhiteSpace(region)) {
      this.regionalService.getRegionalMasks(region).pipe(map(regionalData => {
        Object.keys(abstractControl.parent.controls).forEach(key => {
          const frm = (abstractControl.parent as UntypedFormGroup);
          const ctrl = frm.get(key);
          if (ctrl.controlData) {
            switch (ctrl.controlData.type) {
              case AccelaControlType.Zip:
                const zipVal = ctrl.value;
                ctrl.controlData.mask = regionalData.ZipMask ?? ctrl.controlData.mask;
                if (zipVal) {
                  ctrl.controlData.value = zipVal;
                  setTimeout(() => {
                    ctrl.setValue(zipVal);
                  }, 0);
                }
                break;
              case AccelaControlType.Phone:
                const phoneVal = ctrl.value;
                ctrl.controlData.mask = regionalData.PhoneMask ?? ctrl.controlData.mask;
                if (phoneVal) {
                  ctrl.controlData.value = phoneVal;
                  setTimeout(() => {
                    ctrl.setValue(phoneVal);
                  }, 0);
                }
                break;
              case AccelaControlType.Dropdown:
              case AccelaControlType.Textbox:
                if (ctrl.controlData.name === 'txtState'
                  || ctrl.controlData.name === 'ddlAppState'
                  || ctrl.controlData.name === 'txtAppState') {
                  const stateVal = ctrl.value;
                  const arry = regionalData.States as Array<SelectItem>;
                  if (Array.isArray(arry)) {
                    ctrl.controlData.type = AccelaControlType.Dropdown;
                    ctrl.controlData.options = arry;
                    if (stateVal) {
                      ctrl.patchValue(stateVal);
                    }
                  } else {
                    ctrl.controlData.type = AccelaControlType.Textbox;
                    if (stateVal) {
                      ctrl.setValue(stateVal);
                    }
                  }

                }
                break;
              default:
                break;
            }
          }
        });
      })).subscribe();
    }
  }

  getAPOTemplateData(type: number): Promise<TemplateAttributeModel[]> {
    const httpParams = new HttpParams()
      .set('Type', type.toString());
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetOriginalTemplateData`), { headers: this.headers, params: httpParams })
      .pipe(map((result) => (result as TemplateAttributeModel[])
      )).toPromise();
  }
}
